import type { ChecklistItemId, TaskId, UserId } from '../../types';

import { fanoutLast } from '../fanout';
import { addToIndex } from '../indexes';
import { maxSortKey, snapshot, sortKeyBetween, taskKeys } from '../internal';
import { logActivity } from '../journal';
import { bump, requireCache } from '../runtime';

export function createChecklistItem(args: { id: ChecklistItemId; taskId: TaskId; text: string }): void {
  const cache = requireCache();
  const task = cache.tasks.get(args.taskId);
  if (task === undefined) {
    throw new Error('Задача не найдена');
  }
  const now = cache.nowMs();
  const siblings: string[] = [];
  for (const id of cache.index.checklistByTask.get(args.taskId) ?? []) {
    const sibling = cache.checklist.get(id);
    if (sibling !== undefined && sibling.deletedAt === null) {
      siblings.push(sibling.sortKey);
    }
  }
  const item = {
    id: args.id,
    taskId: args.taskId,
    text: args.text,
    assigneeId: null,
    creatorId: cache.me,
    done: false,
    doneAt: null,
    sortKey: sortKeyBetween(maxSortKey(siblings), null),
    deletedAt: null,
    createdAt: now,
    updatedAt: now,
  };
  cache.checklist.set(item.id, item);
  addToIndex(cache, 'checklistItem', String(item.id));
  logActivity(cache, {
    mutator: 'checklist.create',
    entityTable: 'checklistItem',
    entityId: String(item.id),
    op: 'insert',
    before: null,
    after: snapshot(item),
    taskIds: [args.taskId],
  });
  fanoutLast(cache);
  bump(...taskKeys(cache, args.taskId));
}

export function toggleChecklistItem(args: { id: ChecklistItemId }): void {
  const cache = requireCache();
  const item = cache.checklist.get(args.id);
  if (item === undefined) {
    throw new Error('Пункт чеклиста не найден');
  }
  const before = { done: item.done, doneAt: item.doneAt, updatedAt: item.updatedAt };
  item.done = !item.done;
  item.doneAt = item.done ? cache.nowMs() : null;
  item.updatedAt = cache.nowMs();
  logActivity(cache, {
    mutator: 'checklist.toggle',
    entityTable: 'checklistItem',
    entityId: String(item.id),
    op: 'update',
    before,
    after: { done: item.done, doneAt: item.doneAt, updatedAt: item.updatedAt },
    taskIds: [item.taskId],
  });
  fanoutLast(cache);
  bump(...taskKeys(cache, item.taskId));
}

export function renameChecklistItem(args: { id: ChecklistItemId; text: string }): void {
  const cache = requireCache();
  const item = cache.checklist.get(args.id);
  if (item === undefined) {
    throw new Error('Пункт чеклиста не найден');
  }
  const next = args.text.trim();
  if (next.length === 0) return;
  const before = { text: item.text, updatedAt: item.updatedAt };
  item.text = next.slice(0, 500);
  item.updatedAt = cache.nowMs();
  logActivity(cache, {
    mutator: 'checklist.rename',
    entityTable: 'checklistItem',
    entityId: String(item.id),
    op: 'update',
    before,
    after: { text: item.text, updatedAt: item.updatedAt },
    taskIds: [item.taskId],
  });
  fanoutLast(cache);
  bump(...taskKeys(cache, item.taskId));
}

export function deleteChecklistItem(args: { id: ChecklistItemId }): void {
  const cache = requireCache();
  const item = cache.checklist.get(args.id);
  if (item === undefined) {
    throw new Error('Пункт чеклиста не найден');
  }
  if (item.deletedAt !== null) return;
  const before = { deletedAt: item.deletedAt };
  item.deletedAt = cache.nowMs();
  item.updatedAt = cache.nowMs();
  const list = cache.index.checklistByTask.get(item.taskId);
  if (list !== undefined) {
    cache.index.checklistByTask.set(
      item.taskId,
      list.filter((id) => id !== item.id),
    );
  }
  logActivity(cache, {
    mutator: 'checklist.delete',
    entityTable: 'checklistItem',
    entityId: String(item.id),
    op: 'update',
    before,
    after: { deletedAt: item.deletedAt },
    taskIds: [item.taskId],
  });
  fanoutLast(cache);
  bump(...taskKeys(cache, item.taskId));
}

export function moveChecklistItem(args: { id: ChecklistItemId; beforeId?: ChecklistItemId; afterId?: ChecklistItemId }): void {
  const cache = requireCache();
  const item = cache.checklist.get(args.id);
  if (item === undefined) {
    throw new Error('Пункт чеклиста не найден');
  }
  const before = { sortKey: item.sortKey, updatedAt: item.updatedAt };
  const beforeKey = args.beforeId !== undefined ? cache.checklist.get(args.beforeId)?.sortKey ?? null : null;
  const afterKey = args.afterId !== undefined ? cache.checklist.get(args.afterId)?.sortKey ?? null : null;
  item.sortKey = sortKeyBetween(beforeKey, afterKey);
  item.updatedAt = cache.nowMs();
  logActivity(cache, {
    mutator: 'checklist.move',
    entityTable: 'checklistItem',
    entityId: String(item.id),
    op: 'update',
    before,
    after: { sortKey: item.sortKey, updatedAt: item.updatedAt },
    taskIds: [item.taskId],
  });
  fanoutLast(cache);
  bump(...taskKeys(cache, item.taskId));
}

export function setChecklistAssignee(args: { id: ChecklistItemId; assigneeId: UserId | null }): void {
  const cache = requireCache();
  const item = cache.checklist.get(args.id);
  if (item === undefined) {
    throw new Error('Пункт чеклиста не найден');
  }
  const before = { assigneeId: item.assigneeId };
  item.assigneeId = args.assigneeId;
  logActivity(cache, {
    mutator: 'checklist.setAssignee',
    entityTable: 'checklistItem',
    entityId: String(item.id),
    op: 'update',
    before,
    after: { assigneeId: item.assigneeId },
    taskIds: [item.taskId],
  });
  fanoutLast(cache);
  bump(...taskKeys(cache, item.taskId), 'inbox');
}
